/**
 * cart-subscribe.js
 * ──────────────────────────────────────────────────────────────────────
 * Powers the "Subscribe and save X%" widget on cart-drawer line items.
 *
 * UX:
 *  - Each eligible line item shows a checkbox: "Subscribe and save X%"
 *    with a strikethrough one-time price and the discounted subscription
 *    price.
 *  - Checking it converts that line item to the default selling plan
 *    (e.g. 5% off recurring delivery) via /cart/change.js.
 *  - If the variant has more than one selling plan (e.g. different
 *    delivery frequencies), a "Delivery frequency" <select> appears once
 *    subscribed, letting the customer change frequency without leaving
 *    the cart.
 *  - Unchecking the box reverts the line item to a one-time purchase
 *    (selling_plan: null).
 *
 * RE-RENDERING — handled entirely by this file:
 *  - Udesly's pipeline does NOT rebuild .cart-list_items after
 *    /cart/change.js (only the native checkbox's own :checked state
 *    changes — nothing else). So after every change we:
 *      1. Update the per-line unit price + selling-plan label.
 *      2. Rebuild just the .cart-subscribe widget for that line via
 *         window.buildCartSubscribeWidget (defined in cart-drawer.liquid).
 *      3. Update the checkout total(s) and the free-shipping bar.
 *
 * LAZY DATA — handled entirely by this file:
 *  - window.cartSubscribeData is seeded once at page load for whatever
 *    was already in the cart. When a NEW item is added via /cart/add,
 *    we fetch that product's .js endpoint to pull its
 *    variant.selling_plan_allocations and inject the widget for that
 *    line on the fly.
 *
 * Event listeners are bound via delegation on `document`.
 * ──────────────────────────────────────────────────────────────────────
 */

const SELECTORS = {
  list: '.cart-list_items',
  widget: '[data-cart-subscribe]',
  toggle: '[data-subscribe-toggle]',
  frequency: '[data-subscribe-frequency]',
  unitPrice: '.cart_price.in-cart',
  planLabel: '.is--per-delivery',
  checkoutTotal: '[udy-el="cart-total"]',
};

function money(cents) {
  return '£' + (Number(cents) / 100).toFixed(2);
}

/**
 * Update a single cart line's selling plan.
 * Passing `sellingPlanId = null` reverts the line to a one-time purchase.
 */
async function setLineSellingPlan(line, sellingPlanId) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      line: line,
      selling_plan: sellingPlanId || null,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(
      data?.description || data?.message || 'Unable to update your cart.'
    );
  }

  return data;
}

/**
 * Make sure window.cartSubscribeData has an entry for this variant.
 * Resolves once data is available (no-op if it already exists).
 */
async function ensureSubscribeData(item) {
  window.cartSubscribeData = window.cartSubscribeData || {};

  if (window.cartSubscribeData[item.variant_id]) return;
  if (!item.url) return;

  try {
    const productUrl = item.url.split('?')[0].replace(/\/$/, '') + '.js';
    const response = await fetch(productUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;

    const product = await response.json();
    const variant = (product.variants || []).find((v) => v.id === item.variant_id);
    if (!variant || !variant.selling_plan_allocations) return;

    // selling_plan_allocations on the product JSON don't include the
    // plan's display name — build a lookup from selling_plan_groups.
    const planNames = {};
    (product.selling_plan_groups || []).forEach((group) => {
      (group.selling_plans || []).forEach((plan) => {
        planNames[plan.id] = plan.name;
      });
    });

    window.cartSubscribeData[item.variant_id] = variant.selling_plan_allocations.map((allocation) => ({
      id: allocation.selling_plan_id,
      name: planNames[allocation.selling_plan_id] || '',
      price: allocation.price,
      compare_at_price: allocation.compare_at_price,
    }));
  } catch (error) {
    console.error('[cart-subscribe] failed to load selling plans for variant', item.variant_id, error);
  }
}

/**
 * Update the unit price + selling-plan label for a line item, and
 * rebuild its .cart-subscribe widget to reflect the latest cart state.
 */
function refreshLineItem(li, item) {
  const priceEl = li.querySelector(SELECTORS.unitPrice);
  if (priceEl) priceEl.textContent = money(item.final_line_price);

  li.querySelectorAll(SELECTORS.planLabel).forEach((el) => {
    el.textContent = item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.name : '';
  });

  if (typeof window.buildCartSubscribeWidget !== 'function') return;

  const html = window.buildCartSubscribeWidget(item);
  const existing = li.querySelector(SELECTORS.widget);

  if (html) {
    if (existing) {
      existing.outerHTML = html;
    } else {
      li.querySelector('.cart-items')?.insertAdjacentHTML('afterend', html);
    }
  } else if (existing) {
    existing.remove();
  }
}

function updateCartTotals(cart) {
  document.querySelectorAll(SELECTORS.checkoutTotal).forEach((el) => {
    el.textContent = money(cart.total_price);
  });

  if (typeof window.updateFreeShippingBar === 'function') {
    window.updateFreeShippingBar(cart);
  }
}

function setLoading(widget, isLoading) {
  if (isLoading) {
    widget.setAttribute('data-loading', '');
    widget.querySelectorAll('input, select').forEach((el) => {
      el.setAttribute('data-loading', '');
      el.disabled = true;
    });
  } else {
    widget.removeAttribute('data-loading');
    widget.querySelectorAll('input, select').forEach((el) => {
      el.removeAttribute('data-loading');
      el.disabled = false;
    });
  }
}

function resolveSellingPlanId(widget, target) {
  const isToggle = target.matches(SELECTORS.toggle);
  const isFrequency = target.matches(SELECTORS.frequency);

  if (isFrequency) {
    return target.value;
  }

  if (isToggle) {
    if (!target.checked) return null;

    const frequencySelect = widget.querySelector(SELECTORS.frequency);
    return frequencySelect
      ? frequencySelect.value
      : widget.dataset.defaultSellingPlan;
  }

  return null;
}

async function handleChange(event) {
  const target = event.target;
  if (!target.matches(SELECTORS.toggle) && !target.matches(SELECTORS.frequency)) {
    return;
  }

  const widget = target.closest(SELECTORS.widget);
  if (!widget) return;

  const line = parseInt(widget.dataset.line, 10);
  if (!line) return;

  const sellingPlanId = resolveSellingPlanId(widget, target);

  setLoading(widget, true);

  try {
    const cart = await setLineSellingPlan(line, sellingPlanId);
    await syncCartList(cart);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  } catch (error) {
    console.error('[cart-subscribe]', error);

    // Roll back the control to its previous state on failure.
    if (target.matches(SELECTORS.toggle)) {
      target.checked = !target.checked;
    }

    setLoading(widget, false);
  }
}

document.addEventListener('change', handleChange);

/**
 * Poll until the <li> count in .cart-list_items matches `expectedCount`,
 * then resolve. Times out after 2s to avoid hanging forever.
 */
function waitForLineItems(list, expectedCount) {
  return new Promise((resolve) => {
    const deadline = Date.now() + 2000;

    (function check() {
      const items = list.querySelectorAll(':scope > li');
      if (items.length >= expectedCount || Date.now() > deadline) {
        resolve(items);
      } else {
        setTimeout(check, 30);
      }
    })();
  });
}

/**
 * After any cart mutation, re-sync every visible line item with the
 * fresh cart object. Waits until Udesly has rendered the correct number
 * of <li> elements before injecting the Subscribe & Save widgets.
 */
async function syncCartList(cart) {
  const list = document.querySelector(SELECTORS.list);
  if (!list) return;

  const liItems = await waitForLineItems(list, cart.items.length);

  await Promise.all(
    cart.items.map(async (item, index) => {
      item.line = index + 1;
      await ensureSubscribeData(item);
      const li = liItems[index];
      if (li) refreshLineItem(li, item);
    })
  );

  updateCartTotals(cart);
}

/**
 * Sync the cart whenever new <li> elements appear inside .cart-list_items.
 * Uses a two-stage observer:
 *   Stage 1 — watch document.body for .cart-list_items to appear (handles
 *             the case where the cart drawer is rendered after JS runs,
 *             e.g. when the cart is empty on page load).
 *   Stage 2 — once .cart-list_items exists, watch it for new <li> children
 *             (handles add-to-cart from any mechanism Udesly uses).
 */
(function initCartObserver() {
  let listObserver = null;
  let syncTimer = null;

  function triggerSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => syncCartList(cart))
        .catch(() => {});
    }, 50);
  }

  function attachListObserver(list) {
    if (listObserver) return; // already watching

    listObserver = new MutationObserver((mutations) => {
      const addedLi = mutations.some((m) =>
        Array.from(m.addedNodes).some((n) => n.nodeName === 'LI')
      );
      if (addedLi) triggerSync();
    });

    listObserver.observe(list, { childList: true });
  }

  // Stage 1: watch for .cart-list_items to appear in the DOM
  const bodyObserver = new MutationObserver(() => {
    const list = document.querySelector(SELECTORS.list);
    if (list) {
      attachListObserver(list);
      // Don't disconnect bodyObserver — if the cart drawer is fully
      // re-mounted by Udesly (e.g. after a page section swap), the list
      // element will be a new node and we need to re-attach.
    }
  });

  bodyObserver.observe(document.body, { childList: true, subtree: true });

  // Also attach immediately if .cart-list_items is already in the DOM
  const list = document.querySelector(SELECTORS.list);
  if (list) attachListObserver(list);
})();